import { customElement } from 'lit/decorators.js';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { getInternalId } from '../../../webmodule-common/other/ui/databinding/databinding';
import { html } from 'lit';
import { LangAppNoBranches } from '../../../localization/scripts/dealer-franchisee/pages/app-no-branches-lang';
import { PageBase } from './helpers/page-base';
import { pages } from '../page-constants';
import { PreventAndRedirectCommands, Router, RouterLocation } from '@vaadin/router';
import { urlForName } from '../../../webmodule-common/other/router';
import { userDataStore } from '../common/current-user-data-store';

function goToSettings(e: Event) {
  e.preventDefault();
  Router.go(urlForName(pages.settings));
}

/**
 * Landing page shown when the signed-in user has no usable branches.
 * Explains why the rest of the app is inaccessible and what to do next.
 */
@customElement('app-no-branches')
export class AppNoBranches extends PageBase {
  internalId: string = getInternalId();

  private get elementId(): string {
    return `no-branches-page-${this.internalId}`;
  }

  /** This page is the branch-guard destination, so it must not re-run the guard. */
  protected requiresAvailableBranches(): boolean {
    return false;
  }

  /**
   * Refresh branch access on enter and return home once a usable branch is available.
   * The app shell owns park/unpark routing; this page only self-redirects when
   * a usable branch is already present at enter time.
   */
  async onBeforeEnter(location: RouterLocation, commands: PreventAndRedirectCommands, router: Router): Promise<any> {
    if (getCurrentUser()) {
      await userDataStore.refreshCoreDetailsAndBranchAccess();
      if (userDataStore.hasAvailableBranches()) {
        return commands.redirect(urlForName(pages.home));
      }
    }

    return await super.onBeforeEnter(location, commands, router);
  }

  protected meta() {
    return {
      title: () => LangAppNoBranches.pageTitle(),
      description: LangAppNoBranches.noBranchesMessage()
    };
  }

  render() {
    if (!this.loggedIn) return html``;

    return html`
      <div id=${this.elementId} class="page-content no-branches-page">
        <div class="page-container">
          <section class="no-branches-card" aria-labelledby="no-branches-title">
            <div class="no-branches-hero">
              <div class="no-branches-icon-wrap" aria-hidden="true">
                <webmodule-icon library="fa" name="fas-store-slash"></webmodule-icon>
              </div>
              <h1 id="no-branches-title">${LangAppNoBranches.noBranchesTitle()}</h1>
              <p class="no-branches-subtitle">${LangAppNoBranches.noBranchesMessage()}</p>
            </div>

            <div class="no-branches-body">
              <h2>${LangAppNoBranches.whatYouCanDo()}</h2>
              <ol class="no-branches-steps">
                ${userDataStore.canChangeBranchHidden
                  ? html`
                      <li class="no-branches-step">
                        <span class="no-branches-step-num">1</span>
                        <div>
                          <strong>${LangAppNoBranches.stepGoToSettingsTitle()}</strong>
                          <p>
                            ${LangAppNoBranches.stepGoToSettingsDetail()}
                            <a href=${urlForName(pages.settings)} @click=${goToSettings}>
                              ${LangAppNoBranches.stepGoToSettingsLink()}
                            </a>
                          </p>
                        </div>
                      </li>
                    `
                  : html`
                      <li class="no-branches-step">
                        <span class="no-branches-step-num">1</span>
                        <div>
                          <strong>${LangAppNoBranches.stepContactAdminTitle()}</strong>
                          <p>${LangAppNoBranches.stepContactAdminDetail()}</p>
                        </div>
                      </li>
                    `}
                <li class="no-branches-step">
                  <span class="no-branches-step-num">2</span>
                  <div>
                    <strong>${LangAppNoBranches.stepSignOutTitle()}</strong>
                    <p>${LangAppNoBranches.stepSignOutDetail()}</p>
                  </div>
                </li>
              </ol>

              <p class="no-branches-note">${LangAppNoBranches.whyThisHappens()}</p>
            </div>
          </section>
        </div>
      </div>
    `;
  }
}
